import { createContext, useState, useContext, useEffect, useRef } from "react";
import { checkAuth, logout } from "../api";
import { createSearchParams, useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import loadingAnimation from "../Animations/load-animation.json";
import PropTypes from "prop-types";

// Year Context
export const YearContext = createContext();

// Auth Context
export const AuthContext = createContext();

// Custom hook for auth context
export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}

export function YearProvider({ children }) {
  const [selectedYear, setSelectedYear] = useState(() => {
    const cachedYear = localStorage.getItem("selectedYear");
    return cachedYear ? Number(cachedYear) : new Date().getFullYear();
  });
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasSetInitialYear = useRef(false);
  const { loggedIn, userData } = useAuthContext();

  useEffect(() => {
    if (!loggedIn || !userData?.goals) return;

    try {
      // Extract unique years from goals and sort them in descending order
      const uniqueYears = [
        ...new Set(userData.goals.map((goal) => goal.year)),
      ].sort((a, b) => b - a);
      setYears(uniqueYears);

      const savedYear = localStorage.getItem("selectedYear");

      if (savedYear) {
        setSelectedYear(Number(savedYear));
      } else if (uniqueYears.length > 0 && !hasSetInitialYear.current) {
        setSelectedYear(uniqueYears[0]);
        hasSetInitialYear.current = true;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loggedIn, userData]);

  const updateSelectedYear = (year) => {
    setSelectedYear(year);
    localStorage.setItem("selectedYear", year);
  };

  return (
    <YearContext.Provider
      value={{
        selectedYear,
        setSelectedYear: updateSelectedYear,
        years,
        loading,
        error,
      }}
    >
      {children}
    </YearContext.Provider>
  );
}

YearProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Separate loading component
function LoadingScreen() {
  return (
    <div className="fixed inset-0 bg-white flex items-center justify-center">
      <div className="text-center">
        <Lottie
          animationData={loadingAnimation}
          loop
          autoplay
          style={{ width: 300, height: 300 }}
        />
        <p className="text-center text-lg">Loading your data...</p>
      </div>
    </div>
  );
}

export function AuthProvider({ children }) {
  const [loggedIn, setLoggedIn] = useState(false);
  const [userData, setUserData] = useState(() => {
    const cachedData = localStorage.getItem("userData");
    return cachedData ? JSON.parse(cachedData) : null;
  });
  const [authStatus, setAuthStatus] = useState("loading");
  const [dashboardData, setDashboardData] = useState(null);
  const navigate = useNavigate();
  const authCheckRef = useRef(false);

  // Fetch currencies and update userData
  const updateUserDataWithCurrencies = async (currentUserData) => {
    try {
      // Use default currencies without making an API call
      const defaultCurrencies = [
        "EUR",
        "USD",
        "PLN",
        "DKK",
        "GBP",
        "CAD",
        "AUD",
        "CHF",
        "JPY",
        "SEK",
        "NOK",
      ];

      // console.log("Using default currencies:", defaultCurrencies);

      const updatedUserData = {
        ...currentUserData,
        currencies: defaultCurrencies,
      };
      setUserData(updatedUserData);
      localStorage.setItem("userData", JSON.stringify(updatedUserData));
      return updatedUserData;
    } catch (error) {
      console.error("Error setting default currencies:", error);
      // Use default currencies on error
      const defaultCurrencies = [
        "EUR",
        "USD",
        "PLN",
        "DKK",
        "GBP",
        "CAD",
        "AUD",
        "CHF",
        "JPY",
        "SEK",
        "NOK",
      ];
      const updatedUserData = {
        ...currentUserData,
        currencies: defaultCurrencies,
      };
      setUserData(updatedUserData);
      localStorage.setItem("userData", JSON.stringify(updatedUserData));
      return updatedUserData;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      if (authCheckRef.current) return;
      authCheckRef.current = true;

      try {
        // First try to use cached data if available
        const cachedData = localStorage.getItem("userData");
        if (cachedData) {
          try {
            const parsedData = JSON.parse(cachedData);
            // Ensure authMethod is present in cached data
            if (!parsedData.authMethod && parsedData.email) {
              // If authMethod is missing but we have email, fetch fresh data
              const response = await checkAuth();
              if (response && response.data) {
                const updatedData = await updateUserDataWithCurrencies(
                  response.data
                );
                setUserData(updatedData);
                setAuthStatus("success");
                setLoggedIn(true);
                return;
              }
            }
            // If currencies are not in cached data, fetch them
            if (!parsedData.currencies) {
              const updatedData =
                await updateUserDataWithCurrencies(parsedData);
              setUserData(updatedData);
            } else {
              setUserData(parsedData);
            }
            setAuthStatus("success");
            setLoggedIn(true);
          } catch (error) {
            console.error("Error parsing cached user data:", error);
            // Instead of immediate logout, try to fetch fresh data
            try {
              const response = await checkAuth();
              if (response && response.data) {
                const updatedData = await updateUserDataWithCurrencies(
                  response.data
                );
                setUserData(updatedData);
                setAuthStatus("success");
                setLoggedIn(true);
                return;
              }
            } catch (authError) {
              console.error(
                "Failed to recover from cached data error:",
                authError
              );
              handleLogout();
            }
          }
        }

        // Always verify with backend
        try {
          const response = await checkAuth();
          if (response && response.data) {
            // Ensure authMethod is present in the response
            const userData = {
              ...response.data,
              authMethod: response.data.authMethod || "email", // Default to 'email' if not specified
            };
            // Fetch currencies and update userData
            const updatedData = await updateUserDataWithCurrencies(userData);
            setUserData(updatedData);
            
            // Dashboard data will be loaded separately when needed
            // This reduces initial load time and memory usage
            
            setAuthStatus("success");
            setLoggedIn(true);
          } else {
            throw new Error("Invalid response from auth check");
          }
        } catch (error) {
          // console.error("Auth check failed:", error);
          handleLogout();
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        handleLogout();
      }
    };

    initializeAuth();

    // Cleanup function
    return () => {
      authCheckRef.current = false;
    };
  }, []);

  const refetchUserData = async () => {
    try {
      const response = await checkAuth();
      if (response && response.data) {
        // Ensure authMethod is present in the response
        const userData = {
          ...response.data,
          authMethod: response.data.authMethod || "email", // Default to 'email' if not specified
        };
        // Fetch currencies and update userData
        const updatedData = await updateUserDataWithCurrencies(userData);
        setUserData(updatedData);
        setAuthStatus("success");
        setLoggedIn(true);
      } else {
        throw new Error("Invalid response from auth check");
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      handleLogout();
    }
  };

  const handleLogout = async (redirectTo) => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Always clean up local state and storage, regardless of server response
      localStorage.removeItem("userData");
      setLoggedIn(false);
      setAuthStatus("failure");
      setUserData(null);

      // Only redirect to login if we're not already on a public route
      const currentPath = window.location.pathname;
      const publicRoutes = [
        "/login",
        "/register",
        "/forgot-password",
        "/forgot-password-expired",
        "/reset-password",
        "/register-resend-link",
        "/email-change-expired",
      ];

      const isPublicRoute = publicRoutes.some(
        (route) =>
          currentPath === route ||
          currentPath.startsWith("/reset-password/") ||
          currentPath.startsWith("/register-resend-link/")
      );

      if (redirectTo) {
        window.location.href = redirectTo;
        return;
      }

      if (!isPublicRoute) {
        console.log("Redirecting to login");
        navigate("/login");
      }
    }
  };

  // Add an effect to check auth status periodically
  useEffect(() => {
    if (!loggedIn) return;

    let retryCount = 0;
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 5000; // 5 seconds

    const checkAuthInterval = setInterval(
      async () => {
        try {
          await checkAuth();
          retryCount = 0; // Reset retry count on success
        } catch (error) {
          console.error("Periodic auth check failed:", error);

          // Only increment retry count for network or server errors
          if (
            error.message?.includes("network") ||
            error.message?.includes("server")
          ) {
            retryCount++;

            if (retryCount >= MAX_RETRIES) {
              console.error("Max retries reached for auth check");
              handleLogout();
            } else {
              // Wait before retrying
              await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
              // Retry immediately
              try {
                await checkAuth();
                retryCount = 0; // Reset on successful retry
              } catch (retryError) {
                console.error("Auth check retry failed:", retryError);
                handleLogout();
              }
            }
          } else {
            // For other errors (like invalid token), logout immediately
            handleLogout();
          }
        }
      },
      60 * 60 * 1000
    ); // Check every 1 hour

    return () => {
      clearInterval(checkAuthInterval);
      retryCount = 0;
    };
  }, [loggedIn]);

  useEffect(() => {
    if (userData) {
      localStorage.setItem("userData", JSON.stringify(userData));
    }
  }, [userData]);

  const contextValue = {
    loggedIn,
    setLoggedIn,
    logout: handleLogout,
    userData,
    setUserData,
    authStatus,
    setAuthStatus,
    refetchUserData,
    dashboardData,
    setDashboardData,
  };

  if (authStatus === "loading") {
    return <LoadingScreen />;
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
